import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  categoriesApi, transactionsApi, accountsApi, incomesApi,
  spendingGoalsApi, savingsPlansApi, debtPlansApi,
  membersApi, householdSettingsApi,
} from '../services/api';
import { fmt, cents, sumBy, groupBy } from '../utils/format';
import { computeRTA, computeLiquidTotal, computeLiveOpeningTotal, computeEmergencyFundEnvelopeBonus } from '../utils/calculations';
import { computeEnvelopePlanned } from '../utils/envelopePlanned';
import { classifyCategories, ownedCategoryNames, CLASS, CLASS_COLOR, describeClass } from '../utils/categoryClass';
import Modal from '../components/Modal';

const GROUP_ORDER = [
  'Savings/Investments', 'House costs', 'Utilities', 'Misc. Variable',
  'Non-grocery foods', 'Insurance', 'Subscriptions', 'Debt Payments', 'Credit Card',
];

function sortGroups(groups) {
  return Object.keys(groups).sort((a, b) => {
    const ai = GROUP_ORDER.indexOf(a), bi = GROUP_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1; if (bi === -1) return -1;
    return ai - bi;
  });
}

/**
 * Read-only marker for how the strategy treats this envelope. It reports the
 * model rather than setting it — the class follows whatever in Setup points at
 * the category, so there is nothing here to get out of step with the cut math.
 */
function ClassBadge({ info }) {
  const cls = info?.cls || CLASS.FIXED;
  const c = CLASS_COLOR[cls];
  return (
    <span
      title={describeClass(info) + (info?.conflict
        ? ' — warning: more than one thing in Setup points at this category.' : '')}
      style={{
        fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 99,
        border: `1px solid ${c.border}`, background: c.bg, color: c.fg,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        marginLeft: 8, cursor: 'help', whiteSpace: 'nowrap',
      }}>
      {info?.label || 'Fixed'}{info?.conflict ? ' !' : ''}
    </span>
  );
}

export default function BudgetTab({ month, onRefresh }) {
  const [cats,     setCats]     = useState([]);
  const [txs,      setTxs]      = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [incomes,  setIncomes]  = useState([]);
  const [spendingGoals, setSpendingGoals] = useState([]);
  const [savingsPlans,  setSavingsPlans]  = useState([]);
  const [debtPlans,     setDebtPlans]     = useState([]);
  const [members,       setMembers]       = useState([]);
  const [settings,      setSettings]      = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [showAdd,  setShowAdd]  = useState(false);
  const [newCat,   setNewCat]   = useState({ groupName: '', name: '', planned: '' });
  // Envelope being moved to another group, plus the pending destination
  const [moving,    setMoving]    = useState(null);
  const [moveTo,    setMoveTo]    = useState('');
  const [moveNew,   setMoveNew]   = useState('');
  const [moveFuture, setMoveFuture] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      categoriesApi.getAll(month.id),
      transactionsApi.getAll(month.id),
      accountsApi.getAll(month.id),
      incomesApi.get(month.id),
      // The Setup model decides each envelope's cost class — see utils/categoryClass.
      spendingGoalsApi.getAll(),
      savingsPlansApi.getAll(),
      debtPlansApi.getAll(),
      membersApi.getAll(),
      householdSettingsApi.get(),
    ]).then(([cR, tR, aR, iR, sgR, spR, dpR, mR, hsR]) => {
      setCats(cR.data); setTxs(tR.data); setAccounts(aR.data); setIncomes(iR.data);
      setSpendingGoals(sgR.data); setSavingsPlans(spR.data); setDebtPlans(dpR.data);
      setMembers(mR.data); setSettings(hsR.data);
      setLoading(false);
    });
  }, [month.id]);

  // Ensure permanent / per-account default categories exist — runs once per month.
  // Guarded with a ref so React StrictMode's double-invoke (and re-renders) can't
  // create duplicates:
  //  · Emergency Fund — always present, can't be deleted
  //  · one category per credit-card account (for its payments / starting balance)
  const ensuredMonths = useRef(new Set());
  useEffect(() => {
    if (ensuredMonths.current.has(month.id)) return;
    ensuredMonths.current.add(month.id);           // set synchronously before any await
    (async () => {
      const [cR, aR] = await Promise.all([
        categoriesApi.getAll(month.id),
        accountsApi.getAll(month.id),
      ]);
      const have     = new Set(cR.data.map(c => c.name));
      const toCreate = [];
      if (!have.has('Emergency Fund'))
        toCreate.push({ groupName: 'Savings/Investments', name: 'Emergency Fund', planned: 0, assigned: 0 });
      aR.data.filter(a => a.isCreditCard).forEach(a => {
        if (!have.has(a.name)) {
          have.add(a.name);                          // guard against duplicate CC names
          toCreate.push({ groupName: 'Credit Card', name: a.name, planned: 0, assigned: 0 });
        }
      });
      if (toCreate.length) {
        await Promise.all(toCreate.map(c => categoriesApi.add(month.id, c)));
        load();
      }
    })();
  }, [month.id, load]);

  useEffect(() => { load(); }, [load]);

  // Income comes from the month's income lines (edited in Strategy tab)
  const totalIncome = incomes.reduce((s, r) => s + (r.amount || 0), 0);

  // ── Core calculations ──────────────────────────────────────────────────────
  const rta          = computeRTA(txs, cats, accounts);
  const liquidTotal  = computeLiquidTotal(accounts);
  const openingTotal = computeLiveOpeningTotal(accounts, cats);

  const spent = useCallback((name) =>
    txs.filter(t => t.type === 'Debit' && t.categoryName === name)
       .reduce((s, t) => s + t.amount, 0), [txs]);

  const plannedByName = computeEnvelopePlanned({
    categories: cats, accounts, transactions: txs, members,
    savingsPlans, debtPlans, spendingGoals, settings, incomes,
  });
  const getPlanned = (cat) => cents(plannedByName[cat.name] ?? cat.planned);

  // Keep stored Planned in step with Strategy so a leftover Save-all snapshot
  // cannot outlive the live extra/surplus the user already elected.
  const plannedSyncKey = useRef('');
  useEffect(() => {
    if (loading) return;
    const owned = ownedCategoryNames({ spendingGoals, savingsPlans, debtPlans });
    const dirty = cats.filter(c => owned.has(c.name)
      && Math.abs(cents(plannedByName[c.name]) - cents(c.planned)) >= 0.005);
    const sig = dirty.map(c => `${c.id}:${cents(plannedByName[c.name])}`).join('|');
    if (!dirty.length || sig === plannedSyncKey.current) return;
    plannedSyncKey.current = sig;
    Promise.all(dirty.map(c =>
      categoriesApi.update(c.id, { planned: cents(plannedByName[c.name]) })));
  }, [loading, cats, plannedByName, spendingGoals, savingsPlans, debtPlans]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAssignedChange = async (cat, val) => {
    const assigned = cents(parseFloat(val));
    setCats(prev => prev.map(c => c.id === cat.id ? { ...c, assigned } : c));
    await categoriesApi.update(cat.id, { assigned });
  };

  const handleAddCat = async () => {
    if (!newCat.name.trim()) return;
    await categoriesApi.add(month.id, {
      groupName: newCat.groupName || 'Other',
      name: newCat.name.trim(),
      planned: parseFloat(newCat.planned) || 0,
      assigned: 0,
    });
    setNewCat({ groupName: '', name: '', planned: '' }); setShowAdd(false); load();
  };

  const handleDeleteCat = async (id) => {
    if (!window.confirm('Remove this category?')) return;
    await categoriesApi.delete(id); load();
  };

  const openMove = (cat) => {
    setMoving(cat); setMoveTo(cat.groupName || ''); setMoveNew(''); setMoveFuture(true);
  };

  const handleMove = async () => {
    const target = (moveNew.trim() || moveTo || '').trim();
    if (!target || target === moving.groupName) { setMoving(null); return; }
    await categoriesApi.move(moving.id, target, moveFuture);
    setMoving(null); load();
  };

  // Each envelope's cost class is derived from what in Setup points at it, so
  // there is nothing to toggle here and no way for it to drift out of sync with
  // the strategy math.
  const catClasses = classifyCategories({
    categories: cats, spendingGoals, savingsPlans, debtPlans });

  // Set one category's Assigned to its Planned amount (fill the difference).
  const fillOne = async (cat) => {
    const assigned = cents(getPlanned(cat));
    setCats(prev => prev.map(c => c.id === cat.id ? { ...c, assigned } : c));
    await categoriesApi.update(cat.id, { assigned });
  };

  // Set every category's Assigned to its Planned amount.
  const handleAutoAssign = async () => {
    const changed = cats.filter(c => cents(getPlanned(c)) !== cents(c.assigned));
    if (changed.length === 0) return;
    setCats(prev => prev.map(c => ({ ...c, assigned: cents(getPlanned(c)) })));
    await Promise.all(changed.map(c =>
      categoriesApi.update(c.id, { assigned: cents(getPlanned(c)) })));
  };

  const groups        = groupBy(cats, 'groupName');
  const sortedKeys    = sortGroups(groups);
  const totalAssigned = sumBy(cats, 'assigned');
  const assignDeps    = txs.filter(t => t.categoryName === 'Up for Grabs' && t.type === 'Deposit')
                           .reduce((s, t) => s + t.amount, 0);

  const rtaColor = rta < 0 ? 'var(--red)' : rta === 0 ? 'var(--green)' : 'var(--blue)';
  const rtaBg    = rta < 0 ? 'var(--red-bg)' : rta === 0 ? 'var(--green-bg)' : 'var(--blue-bg)';

  return (
    <>
      {/* Up for Grabs hero */}
      <div style={{ background: rtaBg, border: `2px solid ${rtaColor}`, borderRadius: 14, padding: '20px 28px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: rtaColor, marginBottom: 4 }}>Up for Grabs</div>
          <div style={{ fontSize: 40, fontWeight: 700, color: rtaColor, lineHeight: 1 }}>{fmt(rta)}</div>
          <div style={{ fontSize: 12, color: rtaColor, marginTop: 6, opacity: 0.85 }}>
            {rta < 0 && "You've grabbed more than you've got — trim a category."}
            {rta > 0 && 'Grab this and give every dollar a job.'}
            {rta === 0 && "Every dollar's got a job. ✓"}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 13, color: rtaColor, opacity: 0.9 }}>
          <div>Income in: <strong>{fmt(assignDeps)}</strong></div>
          <div style={{ marginTop: 2 }}>+ Liquid cash: <strong>{fmt(liquidTotal)}</strong></div>
          {openingTotal !== 0 && (
            <div style={{ marginTop: 2 }}>+ Account openings: <strong>{fmt(openingTotal)}</strong></div>
          )}
          <div style={{ marginTop: 2 }}>− Grabbed: <strong>{fmt(totalAssigned)}</strong></div>
        </div>
      </div>

      {/* Income reference bar — view only, edit in Strategy */}
      <div className="card" style={{ padding: '12px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Income</span>
          {incomes.map(r => (
            <span key={r.id ?? r.label}>{r.label} <strong>{fmt(r.amount)}</strong></span>
          ))}
          {incomes.length === 0 && <span style={{ color: 'var(--text3)' }}>No income set</span>}
          <span style={{ fontWeight: 700 }}>Total <strong>{fmt(totalIncome)}</strong></span>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>Edit in Strategy tab · Planned follows Strategy live</span>
        </div>
      </div>

      {/* Envelope table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <span className="sec-label" style={{ margin: 0 }}>Envelopes</span>
            <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 10 }}>
              Planned = live Strategy amount (extra and surplus included) · Grabbed = what you set aside · Available = Grabbed − Spent ·
              the pill shows how cuts treat it — set in Setup
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" onClick={handleAutoAssign}
              title="Set every category's Grabbed amount to its Planned amount">Grab all</button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ Category</button>
          </div>
        </div>
        {loading ? <div className="spinner" /> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="r" style={{ width: 115 }}>Planned</th>
                  <th className="r" style={{ width: 115 }}>Grabbed</th>
                  <th className="r" style={{ width: 115 }}>Available</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {sortedKeys.map(group => {
                  const items    = groups[group];
                  const gPlanned = items.reduce((s, c) => s + getPlanned(c), 0);
                  const gAssign  = sumBy(items, 'assigned');
                  const groupHasEF = items.some(c => c.name === 'Emergency Fund');
                  const ilBonus  = groupHasEF ? computeEmergencyFundEnvelopeBonus(accounts, txs) : 0;
                  // Snap to cents before any comparison — see format.cents.
                  const gAvail   = cents(gAssign - items.reduce((s, c) => s + spent(c.name), 0) + ilBonus);
                  return (
                    <React.Fragment key={group}>
                      <tr className="group-row"><td colSpan={5}>{group}</td></tr>
                      {items.map(cat => {
                        const s       = spent(cat.name);
                        const isEF    = cat.name === 'Emergency Fund';
                        const ilBonus = isEF ? computeEmergencyFundEnvelopeBonus(accounts, txs) : 0;
                        const avail   = cents((cat.assigned || 0) - s + ilBonus);
                        const plan    = getPlanned(cat);
                        return (
                          <tr key={cat.id}>
                            <td style={{ paddingLeft: 22 }}>
                              {cat.name}
                              {isEF && ilBonus > 0 && (
                                <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>
                                  incl. {fmt(ilBonus)} invested
                                </span>
                              )}
                              <ClassBadge info={catClasses.get(cat.name)} />
                              <button
                                onClick={() => openMove(cat)}
                                title={`Move ${cat.name} to another category group`}
                                style={{
                                  fontSize: 11, padding: '1px 6px', borderRadius: 99,
                                  border: '1px solid var(--border2)', background: 'transparent',
                                  color: 'var(--text3)', cursor: 'pointer', marginLeft: 6,
                                  lineHeight: 1.4,
                                }}>⇄</button>
                            </td>
                            <td className="r muted" style={{ fontSize: 13 }}>{fmt(plan)}</td>
                            <td className="r">
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                                {cents(cat.assigned) !== plan && (
                                  <button className="btn btn-sm" title={`Fill to planned (${fmt(plan)})`}
                                    style={{ fontSize: 11, padding: '1px 6px', lineHeight: 1.4 }}
                                    onClick={() => fillOne(cat)}>=</button>
                                )}
                                <input className="field-input" type="number" step="0.01"
                                  style={{ width: 90, textAlign: 'right', fontSize: 12, padding: '3px 7px',
                                    borderColor: avail < 0 ? 'var(--red)' : undefined }}
                                  value={parseFloat((cat.assigned || 0).toFixed(2))}
                                  onChange={e => handleAssignedChange(cat, e.target.value)} />
                              </div>
                            </td>
                            <td className={`r ${avail < 0 ? 'neg' : avail > 0 ? 'pos' : 'muted'}`}
                              style={{ fontWeight: avail < 0 ? 700 : 400 }}>
                              {fmt(avail)}
                            </td>
                            <td>
                              {!isEF && (
                                <button className="btn btn-danger btn-sm" onClick={() => handleDeleteCat(cat.id)}>✕</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ background: 'var(--surface2)' }}>
                        <td style={{ paddingLeft: 22, fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{group}</td>
                        <td className="r muted" style={{ fontSize: 12 }}>{fmt(gPlanned)}</td>
                        <td className="r muted" style={{ fontSize: 12 }}>{fmt(gAssign)}</td>
                        <td className={`r ${gAvail < 0 ? 'neg' : gAvail > 0 ? 'pos' : 'muted'}`} style={{ fontSize: 12, fontWeight: 600 }}>{fmt(gAvail)}</td>
                        <td></td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                <tr className="total-row">
                  <td><strong>Total</strong></td>
                  <td className="r"><strong>{fmt(cats.reduce((s, c) => s + getPlanned(c), 0))}</strong></td>
                  <td className="r"><strong>{fmt(totalAssigned)}</strong></td>
                  <td className={`r ${rta < 0 ? 'neg' : rta > 0 ? 'pos' : ''}`}><strong>{fmt(rta)}</strong></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {moving && (
        <Modal title={`Move ${moving.name}`} onClose={() => setMoving(null)}>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Move to group</label>
            <select className="field-input" style={{ width: '100%' }} value={moveTo}
              onChange={e => { setMoveTo(e.target.value); setMoveNew(''); }}>
              {sortGroups(Object.fromEntries(
                [...new Set([...GROUP_ORDER, ...cats.map(c => c.groupName).filter(Boolean)])]
                  .map(g => [g, true])
              )).map(g => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>…or a new group</label>
            <input className="field-input" style={{ width: '100%' }} value={moveNew}
              placeholder="Leave blank to use the group above"
              onChange={e => setMoveNew(e.target.value)} />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 16, fontSize: 12.5, color: 'var(--text2)' }}>
            <input type="checkbox" checked={moveFuture} style={{ marginTop: 3 }}
              onChange={e => setMoveFuture(e.target.checked)} />
            <span>
              Apply to later months too — otherwise next month's roll-forward puts
              it back. Months before this one keep the group they had, so your
              trend charts stay honest.
            </span>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleMove}>Move</button>
            <button className="btn" onClick={() => setMoving(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="Add category" onClose={() => setShowAdd(false)}>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Group</label>
            <select className="field-input" style={{ width: '100%' }} value={newCat.groupName}
              onChange={e => setNewCat(p => ({ ...p, groupName: e.target.value }))}>
              <option value="">— select —</option>
              {GROUP_ORDER.map(g => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Name</label>
            <input className="field-input" style={{ width: '100%' }} value={newCat.name}
              onChange={e => setNewCat(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label>Planned (if not auto-computed)</label>
            <input className="field-input" type="number" style={{ width: '100%' }} value={newCat.planned}
              onChange={e => setNewCat(p => ({ ...p, planned: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleAddCat}>Add</button>
            <button className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </Modal>
      )}
    </>
  );
}
